// "Find contacts" on a deal: the addresses the company's website publishes (the Worker's company lookup), then its
// named decision-makers with verified work emails (lead_search find_people: Apify Leads Finder, built from LinkedIn
// profiles), each new one added as a contact of the company and the deal. The do-not-contact list is skipped.
// ponytail: Apollo people search returns no emails on its own (they cost enrichment credits) and the free plan refuses
// it; with a paid plan it slots in beside find_people.
import type { Identifier } from "ra-core";

import { domainOf } from "../../../../../supabase/functions/_shared/leadFilters.ts";
import type { CrmDataProvider } from "../../providers/types";
import { lookupCompany } from "../companyLookup";
import {
  contactFromEmail,
  receivesMail,
  SENIOR,
  type FoundContact,
} from "../../../../../supabase/functions/_shared/outreach.ts";
import { MAX_POLLS, POLL_MS } from "./leads";

export { contactFromEmail, receivesMail, type FoundContact };

type Person = {
  first_name: string;
  last_name?: string;
  title?: string;
  email: string;
  linkedin_url?: string;
};

/** A country code as the people search wants it ("EG" -> "Egypt"); names pass through */
export const countryName = (country?: string | null) => {
  const value = country?.trim();
  if (!value) return undefined;
  if (!/^[A-Za-z]{2}$/.test(value)) return value;
  try {
    return (
      new Intl.DisplayNames(["en"], { type: "region" }).of(
        value.toUpperCase(),
      ) ?? undefined
    );
  } catch {
    return undefined;
  }
};

/**
 * A person worth writing to: a senior title, and an address at the company's own domain (the search matches titles
 * loosely and brings branches abroad, nbeny.com for nbe.com.eg)
 */
export const isDecisionMaker = (person: FoundContact, domain: string) => {
  const at = person.email.split("@")[1] ?? "";
  return (
    (at === domain || at.endsWith(`.${domain}`)) && SENIOR.test(person.title)
  );
};

/** Decision-makers with verified emails at the company's domain and country (Apify, a minute or two) */
export const findPeople = async (
  dataProvider: CrmDataProvider,
  domain: string,
  country?: string | null,
): Promise<FoundContact[]> => {
  let answer = await dataProvider.leadSearch<{
    run_id?: string;
    people?: Person[];
  }>({ action: "find_people", domain, country: countryName(country) });
  for (let i = 0; answer.run_id && !answer.people && i < MAX_POLLS; i++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    answer = {
      run_id: answer.run_id,
      ...(await dataProvider.leadSearch<{ people?: Person[] }>({
        action: "people_status",
        run_id: answer.run_id,
      })),
    };
  }
  if (!answer.people) throw new Error("The people search is still running");
  return answer.people
    .map((p) => ({
      email: p.email,
      first_name: p.first_name,
      last_name: p.last_name ?? "",
      title: p.title ?? "",
      linkedin_url: p.linkedin_url,
    }))
    .filter((p) => isDecisionMaker(p, domain));
};

export const findContacts = async (
  dataProvider: CrmDataProvider,
  input: {
    website: string;
    companyId: Identifier;
    deal?: { id: Identifier; contact_ids?: Identifier[] };
    salesId?: Identifier;
    /** Also search named decision-makers (spends Apify credit, about $0.003 a person) */
    people?: boolean;
    /** The company's country (code or name), so a global domain brings local people */
    country?: string | null;
  },
) => {
  const notes: string[] = [];
  const candidates: FoundContact[] = [];
  try {
    const { emails = [] } = await lookupCompany(input.website);
    candidates.push(...emails.map(contactFromEmail));
  } catch (error) {
    notes.push((error as Error).message);
  }
  const domain = domainOf(input.website);
  if (input.people && domain)
    try {
      candidates.push(
        ...(await findPeople(dataProvider, domain, input.country)),
      );
    } catch (error) {
      notes.push((error as Error).message);
    }
  const added: FoundContact[] = [];
  let known = 0;
  let suppressed = 0;
  let undeliverable = 0;
  const mail = new Map<string, Promise<boolean>>();
  const deliverable = (email: string) => {
    const domain = email.split("@")[1] ?? "";
    if (!mail.has(domain)) mail.set(domain, receivesMail(domain));
    return mail.get(domain)!;
  };
  const contactIds = [...(input.deal?.contact_ids ?? [])];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    const email = candidate.email.toLowerCase();
    if (seen.has(email)) continue;
    seen.add(email);
    if (await dataProvider.isSuppressed(email)) {
      suppressed++;
      continue;
    }
    if (!(await deliverable(email))) {
      undeliverable++;
      continue;
    }
    const { data: same } = await dataProvider.getList("contacts", {
      filter: { "email_fts@ilike": `%${email}%` },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    });
    let id = same[0]?.id;
    if (id) known++;
    else {
      const contact = candidate;
      const now = new Date().toISOString();
      id = (
        await dataProvider.create("contacts", {
          data: {
            first_name: contact.first_name,
            last_name: contact.last_name,
            title: contact.title,
            linkedin_url: contact.linkedin_url,
            company_id: input.companyId,
            email_jsonb: [{ email, type: "Work" }],
            phone_jsonb: [],
            sales_id: input.salesId,
            first_seen: now,
            last_seen: now,
            tags: [],
          },
        })
      ).data.id;
      added.push(contact);
    }
    if (!contactIds.includes(id)) contactIds.push(id);
  }
  if (input.deal && contactIds.length !== (input.deal.contact_ids ?? []).length)
    await dataProvider.update("deals", {
      id: input.deal.id,
      data: { contact_ids: contactIds },
      previousData: input.deal,
    });
  if (undeliverable)
    notes.push(
      `${undeliverable} address${undeliverable > 1 ? "es" : ""} dropped: the domain receives no email`,
    );
  return { found: seen.size, added, known, suppressed, notes };
};
